import { Component, ElementRef, HostListener, ViewChild, computed, inject, input, output, ChangeDetectionStrategy } from '@angular/core';
import { slideToggle } from '@composables/slideToggle.js';
import { AppSettings } from '@services/settings/app-settings.service';
import { CommonModule } from '@angular/common';
import { AuthService } from '@services/core/auth.service';
import { MicrosoftAuthService } from '@services/core/authentication.service';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { SocketService } from '@services/core/socket.service';

@Component({
	selector: 'sidebar',
	templateUrl: './sidebar.component.html',
	styleUrl: './sidebar.component.css',
	standalone: true,
	changeDetection: ChangeDetectionStrategy.Eager,
	imports: [CommonModule, RouterLink, RouterLinkActive],
})
export class SidebarComponent {
	navProfileState = "collapse";
	@ViewChild('sidebarScrollbar', { static: false }) private sidebarScrollbar: ElementRef;
	appSidebarMinifiedToggled = output<boolean>();
	hideMobileSidebar = output<boolean>();
	setPageFloatSubMenu = output<any>();

	appSidebarMobileToggled = output<boolean>();
	appSidebarTransparent = input<boolean>();
	appSidebarGrid = input<boolean>();
	appSidebarFixed = input<boolean>();
	appSidebarMinified = input<boolean>();

	public appSettings: AppSettings = inject(AppSettings);
	authService = inject(AuthService);
	private authMicrosoft = inject(MicrosoftAuthService);
	private socketService = inject(SocketService);

	userData = computed(() => this.authService.userData());
	userPhoto = computed(() => this.authService.userPhoto());
	activeRole = computed(() => this.authService.sharingRole() as any);
	menus = computed(() => (this.authService.sharingMenu() as any[]) ?? []);

	appSidebarFloatSubMenu;
	appSidebarFloatSubMenuHide;
	appSidebarFloatSubMenuHideTime = 250;
	appSidebarFloatSubMenuTop;
	appSidebarFloatSubMenuLeft = '60px';
	appSidebarFloatSubMenuRight;
	appSidebarFloatSubMenuBottom;
	appSidebarFloatSubMenuArrowTop;
	appSidebarFloatSubMenuArrowBottom;
	appSidebarFloatSubMenuLineTop;
	appSidebarFloatSubMenuLineBottom;
	appSidebarFloatSubMenuOffset;
	budgetVersionSelected;

	mobileMode = computed(() => this.appSettings.isMobile());

	sidebarTheme(): string {
		if (this.appSettings.isMobile()) {
			return this.appSettings.appDarkMode ? 'dark' : '';
		}
		return this.appSettings.appSidebarLight ? '' : 'dark';
	}

	userInitials(): string {
		const name = String(this.userData()?.displayName || '').trim();
		if (!name) return 'U';
		const parts = name.split(/\s+/).filter(Boolean);
		const letters = (parts[0]?.[0] || '') + (parts[1]?.[0] || '');
		return letters.toUpperCase() || 'U';
	}
	scrollTop;

	// Cierra sesión del usuario con Microsoft
	logout(): void {
		sessionStorage.setItem('fpv_hold_login', '1');
		this.authMicrosoft.logout().subscribe({
			next: () => this._finishLogout(),
			error: () => this._finishLogout()
		});
	}

	private _finishLogout() {
		this.socketService.announceBrowserSession('logout');
		this.authMicrosoft.clearSesion();
		window.location.assign('/');
	}

	toggleNavProfile(e) {
		e.preventDefault();
		var targetSidebar = <HTMLElement>document.querySelector('.app-sidebar:not(.app-sidebar-end)');
		var targetMenu = e.target.closest('.menu-profile');
		var targetProfile = <HTMLElement>document.querySelector('#appSidebarProfileMenu');
		var expandTime = (targetSidebar && targetSidebar.getAttribute('data-disable-slide-animation')) ? 0 : 250;

		if (targetProfile && targetProfile.style) {
			if (targetProfile.style.display == 'block') {
				targetMenu.classList.remove('active');
			} else {
				targetMenu.classList.add('active');
			}
			slideToggle(targetProfile, expandTime);
			targetProfile.classList.toggle('expand');
		}
	}

	toggleAppSidebarMinified() {
		this.appSidebarMinifiedToggled.emit(true);
		this.navProfileState = "collapse";
		this.scrollTop = 40;
	}

	toggleAppSidebarMobile() {
		this.appSidebarMobileToggled.emit(true);
	}

	/** Ruta absoluta para el menú (evita quedarse en /historial por links relativos). */
	menuLink(url: string | null | undefined): string {
		const raw = String(url || '').trim();
		if (!raw) return '/';
		return raw.startsWith('/') ? raw : `/${raw}`;
	}

	// Cierra el sidebar móvil al seleccionar un enlace final
	closeMobileSidebar(): void {
		if (this.appSettings.isMobile()) {
			this.appSettings.appSidebarMobileToggled = false;
		}
	}

	calculateAppSidebarFloatSubMenuPosition() {
		const targetTop = this.appSidebarFloatSubMenuOffset.top;
		const windowHeight = window.innerHeight;

		setTimeout(() => {
			const targetElm = <HTMLElement>document.querySelector('.app-sidebar-float-submenu-container');
			const targetSidebar = <HTMLElement>document.getElementById('sidebar');
			if (!targetElm || !targetSidebar) return;

			this.appSidebarFloatSubMenuLeft = (this.appSidebarFloatSubMenuOffset.width + targetSidebar.offsetLeft) + 'px';
			this.appSidebarFloatSubMenuRight = 'auto';

			this._adjustMenuVerticalPosition(windowHeight, targetTop, targetElm.offsetHeight);
		}, 0);
	}

	private _adjustMenuVerticalPosition(windowHeight: number, targetTop: number, targetHeight: number) {
		if ((windowHeight - targetTop) > targetHeight) {
			this._setPositionTop(targetTop);
		} else {
			this._setPositionBottom(windowHeight, targetTop);
		}
	}

	private _setPositionTop(targetTop: number) {
		this.appSidebarFloatSubMenuTop = targetTop + 'px';
		this.appSidebarFloatSubMenuBottom = 'auto';
		this.appSidebarFloatSubMenuArrowTop = '20px';
		this.appSidebarFloatSubMenuArrowBottom = 'auto';
		this.appSidebarFloatSubMenuLineTop = '20px';
		this.appSidebarFloatSubMenuLineBottom = 'auto';
	}

	private _setPositionBottom(windowHeight: number, targetTop: number) {
		this.appSidebarFloatSubMenuTop = 'auto';
		this.appSidebarFloatSubMenuBottom = '0';
		const arrowBottom = (windowHeight - targetTop) - 21;
		this.appSidebarFloatSubMenuArrowTop = 'auto';
		this.appSidebarFloatSubMenuArrowBottom = arrowBottom + 'px';
		this.appSidebarFloatSubMenuLineTop = '20px';
		this.appSidebarFloatSubMenuLineBottom = arrowBottom + 'px';
	}

	showAppSidebarFloatSubMenu(menu, e) {
		if (this.appSettings.appSidebarMinified) {
			clearTimeout(this.appSidebarFloatSubMenuHide);

			this.appSidebarFloatSubMenu = menu;
			this.appSidebarFloatSubMenuOffset = e.target.getBoundingClientRect();
			this.calculateAppSidebarFloatSubMenuPosition();
		}
	}

	hideAppSidebarFloatSubMenu() {
		this.appSidebarFloatSubMenuHide = setTimeout(() => {
			this.appSidebarFloatSubMenu = '';
		}, this.appSidebarFloatSubMenuHideTime);
	}

	remainAppSidebarFloatSubMenu() {
		clearTimeout(this.appSidebarFloatSubMenuHide);
	}

	@HostListener('scroll', ['$event'])
	onScroll(event) {
		this.scrollTop = (this.appSettings.appSidebarMinified) ? event.srcElement.scrollTop + 40 : 0;
		if (typeof (Storage) !== 'undefined') {
			localStorage.setItem('sidebarScroll', event.srcElement.scrollTop);
		}
	}

	ngAfterViewChecked() {
		if (typeof (Storage) !== 'undefined' && localStorage["sidebarScroll"]) {
			if (this.sidebarScrollbar && this.sidebarScrollbar.nativeElement) {
				this.sidebarScrollbar.nativeElement.scrollTop = localStorage["sidebarScroll"];
			}
		}
	}

	expandCollapseSubmenu(e: Event, currentMenu: any) {
		e.preventDefault();
		e.stopPropagation();
		const item = (e.currentTarget as HTMLElement | null)?.closest?.('.menu-item') as HTMLElement | null;
		const submenu = item?.querySelector(':scope > .menu-submenu') as HTMLElement | null;
		if (submenu) {
			submenu.classList.remove('transitioning');
			['display', 'height', 'overflow', 'padding-top', 'padding-bottom', 'margin-top', 'margin-bottom', 'transition-duration', 'transition-property']
				.forEach((prop) => submenu.style.removeProperty(prop));
		}
		const next = currentMenu.state === 'expand' ? 'collapse' : 'expand';
		// Acordeón en primer nivel: un solo grupo abierto (Configuración vs Seguridad, etc.)
		if (next === 'expand') {
			this.collapseTopLevelSiblings(currentMenu);
		}
		currentMenu.state = next;
	}

	/** Solo cierra hermanos de primer nivel; anidados pueden coexistir. */
	private collapseTopLevelSiblings(currentMenu: any): void {
		const roots = Array.isArray(this.menus()) ? this.menus() : [];
		if (!roots.includes(currentMenu)) return;
		for (const m of roots) {
			if (m !== currentMenu && m?.submenu?.length) {
				m.state = 'collapse';
			}
		}
	}

	isSubmenuOpen(menu: any, isActive = false): boolean {
		if (this.appSettings.appSidebarMinified) return false;
		if (menu?.state === 'collapse') return false;
		if (menu?.state === 'expand') return true;
		return !!isActive;
	}
}
